import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import PageHelp from '../components/help/PageHelp';
import { ClientHeader } from '../components/client/ClientHeader';
import BookingReminderBanner from '../components/BookingReminderBanner';
import { money, fmtDateTime, fmtDate, STATUS_COLOR } from '../components/client/clientShared';
import {
  Calendar, MessageSquare, PawPrint, User, Receipt, Users,
  Loader2, Search, CalendarClock, ChevronRight, Plus, Activity, ArrowRight,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientDashboardPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [providers, setProviders] = useState([]);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [s, u, p] = await Promise.all([
        axios.get(`${API_URL}/api/client/summary`, { headers }),
        axios.get(`${API_URL}/api/client/bookings?scope=upcoming`, { headers }),
        axios.get(`${API_URL}/api/client/providers`, { headers }),
      ]);
      setSummary(s.data || null);
      setUpcoming(u.data || []);
      setProviders(p.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Could not load your dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <ClientHeader />
        <div className="max-w-6xl mx-auto px-4 py-20 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading your dashboard...
        </div>
      </div>
    );
  }

  const inv = summary?.invoices || {};
  const activity = summary?.recentActivity || [];

  return (
    <div className="min-h-screen bg-background">
      <ClientHeader>
        <PageHelp
          testId="client"
          title="Using RanchWorks"
          purpose="Here's how to get the most out of your account."
          actions={[
            { label: 'Find a Provider', desc: 'search and book a provider from the home page' },
            { label: 'Animals', desc: 'save your animals once and reuse them on every booking' },
            { label: 'Bookings', desc: 'see your appointment history any time' },
            { label: 'Invoices', desc: 'open invoices your providers send and see what you owe' },
          ]}
          nextStep="Add your animals so booking is faster next time."
        />
      </ClientHeader>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Booking reminders (RW-BUILD-013) */}
        <BookingReminderBanner bookings={upcoming} onView={(b) => navigate(`/client/bookings/${b.id}`)} />

        {/* Greeting + quick stats */}
        <div>
          <h2 className="text-2xl font-bold" data-testid="client-dashboard-title">
            Welcome back, {user?.name?.split(' ')[0] || 'there'}
          </h2>
          <p className="text-muted-foreground text-sm">Here's a quick look at your account.</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatTile icon={<CalendarClock className="h-5 w-5" />} label="Upcoming" value={summary?.bookings?.upcoming ?? 0}
            onClick={() => navigate('/client/bookings')} testId="stat-upcoming" />
          <StatTile icon={<PawPrint className="h-5 w-5" />} label="My Animals" value={summary?.animals ?? 0}
            onClick={() => navigate('/client/animals')} testId="stat-animals" />
          <StatTile icon={<Users className="h-5 w-5" />} label="Providers" value={summary?.providers ?? 0}
            onClick={() => navigate('/client/providers')} testId="stat-providers" />
          <StatTile icon={<Receipt className="h-5 w-5" />} label="Outstanding" value={money(inv.outstandingCents)}
            onClick={() => navigate('/client/invoices')} testId="stat-outstanding" small />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Upcoming Bookings */}
          <HubCard
            title="Upcoming Bookings" icon={<CalendarClock className="h-4 w-4" />}
            onViewAll={() => navigate('/client/bookings')} testId="card-upcoming"
            count={summary?.bookings?.upcoming}
          >
            {upcoming.length === 0 ? (
              <EmptyMini text="No upcoming appointments" cta="Find a Provider" onCta={() => navigate('/')} />
            ) : (
              <div className="space-y-2">
                {upcoming.slice(0, 3).map((b) => (
                  <div key={b.id} className="border rounded-md p-3" data-testid={`hub-booking-${b.id}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="font-medium text-sm truncate">{b.serviceName || 'Service'}</span>
                      <Badge className={STATUS_COLOR[b.status] || 'bg-gray-100 text-gray-700'}>{b.status}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{fmtDateTime(b.startDateTime)}</p>
                    {b.providerName && <p className="text-xs mt-0.5">with {b.providerName}</p>}
                  </div>
                ))}
              </div>
            )}
          </HubCard>

          {/* Recent Activity */}
          <HubCard title="Recent Activity" icon={<Activity className="h-4 w-4" />} testId="card-activity">
            {activity.length === 0 ? (
              <EmptyMini text="No recent activity yet" />
            ) : (
              <div className="space-y-2">
                {activity.slice(0, 5).map((b) => (
                  <div key={b.id} className="flex items-start gap-2 text-sm" data-testid={`activity-${b.id}`}>
                    <Badge className={`${STATUS_COLOR[b.status] || 'bg-gray-100 text-gray-700'} shrink-0`}>{b.status}</Badge>
                    <div className="min-w-0">
                      <p className="truncate">{b.serviceName || 'Service'}{b.providerName ? ` · ${b.providerName}` : ''}</p>
                      <p className="text-xs text-muted-foreground">{fmtDate(b.startDateTime)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </HubCard>

          {/* Invoices & Payments */}
          <HubCard
            title="Invoices & Payments" icon={<Receipt className="h-4 w-4" />}
            onViewAll={() => navigate('/client/invoices')} testId="card-invoices"
          >
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-md border p-3 bg-red-50/50">
                <span className="text-sm text-muted-foreground">Outstanding</span>
                <span className="font-semibold text-red-700" data-testid="hub-outstanding">{money(inv.outstandingCents)}</span>
              </div>
              <div className="flex items-center justify-between rounded-md border p-3 bg-green-50/50">
                <span className="text-sm text-muted-foreground">Total Paid</span>
                <span className="font-semibold text-green-700" data-testid="hub-paid">{money(inv.paidCents)}</span>
              </div>
              {inv.unpaidCount > 0 && (
                <p className="text-xs text-muted-foreground">
                  {inv.unpaidCount} invoice{inv.unpaidCount !== 1 ? 's' : ''} awaiting payment
                </p>
              )}
            </div>
          </HubCard>

          {/* My Providers */}
          <HubCard
            title="My Providers" icon={<Users className="h-4 w-4" />}
            onViewAll={providers.length > 0 ? () => navigate('/client/providers') : undefined}
            testId="card-providers" count={providers.length}
          >
            {providers.length === 0 ? (
              <EmptyMini text="No providers yet" cta="Find a Provider" onCta={() => navigate('/')} />
            ) : (
              <div className="space-y-2">
                {providers.slice(0, 3).map((p) => (
                  <div key={p.id} className="flex items-center justify-between border rounded-md p-3" data-testid={`hub-provider-${p.id}`}>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{p.displayName}</p>
                      <p className="text-xs text-muted-foreground">
                        {p.bookingCount} booking{p.bookingCount !== 1 ? 's' : ''}
                        {p.city ? ` · ${p.city}${p.state ? ', ' + p.state : ''}` : ''}
                      </p>
                    </div>
                    {p.slug && (
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/providers/${p.slug}`)} data-testid={`view-provider-${p.id}`}>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </HubCard>

          {/* My Animals */}
          <HubCard
            title="My Animals" icon={<PawPrint className="h-4 w-4" />}
            onViewAll={() => navigate('/client/animals')} testId="card-animals" count={summary?.animals}
          >
            <div className="text-sm text-muted-foreground">
              {summary?.animals > 0
                ? `You have ${summary.animals} saved animal${summary.animals !== 1 ? 's' : ''}. Reuse them on any booking.`
                : 'Save your animals once and reuse them when booking with any provider.'}
            </div>
            <Button className="mt-3 w-full" variant="outline" onClick={() => navigate('/client/animals')} data-testid="hub-manage-animals">
              <Plus className="h-4 w-4 mr-1" /> Manage Animals
            </Button>
          </HubCard>

          {/* Quick Actions */}
          <HubCard title="Quick Actions" icon={<Activity className="h-4 w-4" />} testId="card-quick-actions">
            <div className="space-y-2">
              <QuickAction icon={<Search className="h-4 w-4" />} label="Find a Provider" onClick={() => navigate('/')} testId="qa-find-provider" />
              <QuickAction icon={<PawPrint className="h-4 w-4" />} label="Add an Animal" onClick={() => navigate('/client/animals')} testId="qa-add-animal" />
              <QuickAction icon={<CalendarClock className="h-4 w-4" />} label="View Bookings" onClick={() => navigate('/client/bookings')} testId="qa-view-bookings" />
              <QuickAction icon={<MessageSquare className="h-4 w-4" />} label="My Consultations" onClick={() => navigate('/my-consultations')} testId="qa-consultations" />
            </div>
          </HubCard>
        </div>

        {/* Account info */}
        <Card data-testid="client-account-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <User className="h-4 w-4" /> Account
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div><span className="text-muted-foreground">Name: </span><span data-testid="client-name">{user?.name || '—'}</span></div>
            <div><span className="text-muted-foreground">Email: </span><span data-testid="client-email">{user?.email || '—'}</span></div>
            <div><span className="text-muted-foreground">Phone: </span><span data-testid="client-phone">{user?.phone || '—'}</span></div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function StatTile({ icon, label, value, onClick, testId, small }) {
  return (
    <button
      onClick={onClick}
      className="text-left bg-card border rounded-lg p-4 hover:border-primary hover:shadow-sm transition-all"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-muted-foreground mb-1">{icon}<span className="text-xs">{label}</span></div>
      <div className={small ? 'text-xl font-bold' : 'text-2xl font-bold'}>{value}</div>
    </button>
  );
}

function HubCard({ title, icon, count, onViewAll, testId, children }) {
  return (
    <Card data-testid={testId} className="flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            {icon} {title}
            {count != null && count > 0 && <span className="text-xs text-muted-foreground">({count})</span>}
          </CardTitle>
          {onViewAll && (
            <Button variant="ghost" size="sm" onClick={onViewAll} data-testid={`${testId}-view-all`}>
              View all <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex-1">{children}</CardContent>
    </Card>
  );
}

function EmptyMini({ text, cta, onCta }) {
  return (
    <div className="text-center py-4 text-sm text-muted-foreground">
      <p>{text}</p>
      {cta && <Button variant="link" size="sm" onClick={onCta} className="mt-1" data-testid="empty-mini-cta">{cta}</Button>}
    </div>
  );
}

function QuickAction({ icon, label, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 rounded-md border px-3 py-2 text-sm hover:bg-accent transition-colors"
      data-testid={testId}
    >
      <span className="text-primary">{icon}</span>
      <span>{label}</span>
      <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground" />
    </button>
  );
}
